import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Briefcase, 
  Users, 
  Sparkles, 
  ArrowRight, 
  Award, 
  Check,
  Star,
  Quote,
  Phone,
  Calendar,
  MapPin,
  Wifi,
  Presentation,
  Building2
} from 'lucide-react'
import LogoSlider from '../components/LogoSlider'
import EventGallery from '../components/EventGallery'

// Statistiken
const stats = [
  { number: '500+', label: 'Firmenevents', icon: Briefcase },
  { number: '25+', label: 'Jahre Erfahrung', icon: Award },
  { number: '2.000+', label: 'Max. Gäste', icon: Users },
  { number: '4', label: 'Locations', icon: MapPin }
]

// Features
const features = [
  {
    icon: Wifi,
    title: 'Business-Ready',
    description: 'High-Speed WLAN, Beamer, Mikrofon-Anlagen – modernste Technik für Ihre Präsentation.'
  },
  {
    icon: Building2,
    title: '500+ Firmenevents',
    description: 'Von der Weihnachtsfeier bis zum Gala-Dinner – wir kennen die Anforderungen.'
  },
  {
    icon: Users,
    title: '20-2.000+ Personen',
    description: 'Vom Sommerfest bis zum Jahresabschluss – flexibel für jede Unternehmensgröße.'
  },
  {
    icon: Presentation,
    title: 'One-Stop-Solution',
    description: 'Ein Ansprechpartner für alles – Catering, Technik, Service aus einer Hand.'
  }
]

// Event-Typen
const eventTypes = [
  'Sommerfeste',
  'Weihnachtsfeiern',
  'Tagungen & Konferenzen',
  'Workshops',
  'Messeevents',
  'Jubiläen',
  'Produktpräsentationen',
  'Kick-Off Events',
  'Kundenevents',
  'Team-Building',
  'Mitarbeiter-Ehrungen',
  'Gala-Dinner'
]

// Leistungen
const services = [
  'Persönlicher Event-Manager ab Buchung',
  'Professionelle Präsentations-Technik',
  'High-Speed WLAN für alle Gäste',
  'Flexible Bestuhlung (Theater, Bankett, U-Form)',
  'Mikrofon-Anlagen & Sound-Equipment',
  'Große kostenlose Parkplätze',
  'Barrierefreier Zugang',
  'Professionelles Service-Team',
  'Premium-Geschirr & Besteck',
  'Individuelle Dekoration buchbar',
  'Keine Lautstärkeeinschränkungen',
  'Feiern bis Open End!'
]

// Galerie-Bilder aus assets/eventfotos/firmen_gallerie
const firmenGalleryImageFiles = [
  'HK_1071.JPG',
  'HK_766.JPG',
  'HK_798.JPG',
  'LL_1301.JPG',
  'LL_265.JPG'
]

const firmenGalleryImages = firmenGalleryImageFiles.map(fileName => 
  `/assets/eventfotos/firmen_gallerie/${encodeURIComponent(fileName)}`
)

export default function Unternehmensfeiern() {
  const heroRef = useRef<HTMLDivElement>(null)
  
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Compact Banner
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background */}
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/firmenhero.JPG"
            alt="Firmenevent bei Hückels May"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        
        {/* Ruby Glow */}
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Award className="w-3 h-3 text-brand-400" />
                <span className="text-white/70 text-xs uppercase tracking-[0.25em]">500+ Unternehmen vertrauen uns</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Events für <span className="italic text-white/70">Top-Performer</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-sm md:text-base max-w-xl mb-6 leading-relaxed"
          >
            Professionelle Firmenevents mit modernster Technik in historischem Ambiente – von 20 bis 2.000+ Gästen.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="flex flex-row gap-3"
          >
            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-2.5 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500"
              >
                Angebot anfordern
              </motion.button>
            </Link>
            <a href="tel:+4915755537615">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-2.5 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-2"
              >
                Anrufen
                <Phone className="w-3 h-3" />
              </motion.button>
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          STATS BAR
      ════════════════════════════════════════════════════════════ */}
      <section className="border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="py-10 md:py-12 px-6 text-center border-r border-white/5 last:border-r-0 group hover:bg-white/[0.02] transition-colors flex flex-col items-center justify-center h-[140px]"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 mb-3 group-hover:scale-110 transition-transform">
                  <stat.icon className="w-4 h-4 text-brand-400" />
                </div>
                <p className="text-white text-sm font-medium mb-1">{stat.number}</p>
                <p className="text-white/40 text-xs">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          FEATURES GRID
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32">
        <div className="absolute top-20 right-20 w-[400px] h-[400px] bg-brand-800/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              Warum Top-Unternehmen uns wählen
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light"
            >
              Business-Ready, <span className="italic text-white/60">professionell</span>
            </motion.h2>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="h-full p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-brand-800/30 hover:bg-white/[0.04] transition-all duration-500">
                  <div className="w-14 h-14 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-brand-800/20 transition-all duration-500">
                    <item.icon className="w-6 h-6 text-brand-400" />
                  </div>
                  <h3 className="font-serif text-xl text-white font-light mb-3">{item.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          LOGO SLIDER - Partner Unternehmen
      ════════════════════════════════════════════════════════════ */}
      <LogoSlider 
        title="Vertrauen von führenden Unternehmen"
        subtitle="Unsere Partner"
      />

      {/* ════════════════════════════════════════════════════════════
          EVENT TYPES GRID
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              Für jeden Firmen-Anlass
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-3xl md:text-4xl text-white font-light"
            >
              Was möchten Sie <span className="italic text-white/60">feiern?</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {eventTypes.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.03 }}
                className="group"
              >
                <div className="p-5 rounded-xl border border-brand-800/30 bg-white/[0.02] text-center">
                  <p className="text-white text-sm font-medium">{item}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SERVICES - Two Column Layout
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="absolute bottom-20 left-20 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          {/* Feature Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 relative rounded-2xl overflow-hidden h-[300px] md:h-[400px] border border-white/10"
          >
            <img
              src="/assets/home_pics/HK_184.jpg"
              alt="Firmenevent bei Hückels May"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left - Title */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-32"
            >
              <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4">Inklusive Leistungen</p>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-light mb-6 leading-tight">
                Was Sie bekommen
              </h2>
              <div className="h-px w-20 bg-brand-800 mb-6" />
              <p className="text-white/40 leading-relaxed mb-8">
                Transparente Preise. Keine versteckten Kosten. Professioneller Service von A bis Z.
              </p>
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3 bg-brand-800 text-white text-xs uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-700 transition-all duration-500 flex items-center gap-3"
                >
                  Angebot anfordern
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
            </motion.div>

            {/* Right - Services List */}
            <div className="space-y-2">
              {services.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="flex items-center gap-4 p-4 rounded-xl border-l-2 border-brand-800/20 hover:border-brand-800 hover:bg-white/[0.02] transition-all duration-300"
                >
                  <Check className="w-5 h-5 text-brand-400 flex-shrink-0" />
                  <p className="text-white/80">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          TESTIMONIAL
      ════════════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Quote className="w-12 h-12 text-brand-800/30 mx-auto mb-8" />
            
            <blockquote className="font-serif text-2xl md:text-3xl text-white font-light leading-relaxed mb-8 italic">
              "Unsere Firmenfeier hier war ein voller Erfolg – ein Erlebnis, das wir so schnell nicht vergessen werden! 
              Das Essen war köstlich und wurde mit viel Liebe zum Detail zubereitet. Das Team war ausgesprochen herzlich 
              und hat uns jeden Wunsch von den Augen abgelesen. Man fühlt sich direkt wie zu Hause, was in dieser Branche 
              nicht selbstverständlich ist. Die Location selbst ist einladend und rustikal; man merkt, dass viel Wert auf 
              Atmosphäre gelegt wurde. Wir können diesen Ort wärmstens empfehlen!"
            </blockquote>
            
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-brand-400 fill-brand-400" />
              ))}
            </div>
            
            <p className="text-white/40 text-sm uppercase tracking-[0.2em]">
              Heinemann DUS — Firmenfeier
            </p>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          EVENT-GALERIE – FIRMENEVENTS
      ════════════════════════════════════════════════════════════ */}
      <EventGallery
        title="Firmenevents & Business-Highlights"
        subtitle="So sehen echte Unternehmensfeiern bei Hückels May aus – vom Sommerfest bis zur Weihnachtsfeier."
        images={firmenGalleryImages.map((src, index) => ({
          src,
          alt: `Firmenevent bei Hückels May ${index + 1}`
        }))}
      />

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-800/5 rounded-full blur-[150px]" />
        
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Sparkles className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Ihr nächstes Firmen-Highlight</p>
            
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-white font-light mb-8 leading-tight">
              Lassen Sie uns Ihr<br />
              <span className="italic text-white/60">Event planen</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              500+ Unternehmen haben bereits bei uns gefeiert. Kostenlose Beratung und unverbindliches Angebot in max. 48h.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3"
                >
                  <Phone className="w-4 h-4" />
                  Jetzt anfragen
                </motion.button>
              </Link>
            </div>
            
            <p className="mt-8 text-white/30 text-xs uppercase tracking-[0.2em]">
              Telefonische Beratung · Angebot in max. 48h · Besichtigung möglich
            </p>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
